import { Box, Stack, Text, useTheme } from '@adamdotai/design-system'
import {
    nodes as AtMentionNodes,
    AtMentionsPlugin,
} from '@adamdotai/editor/AtMentionsPlugin'
import { AutoLinkPlugin } from '@adamdotai/editor/AutoLinkPlugin'
import { ClickableLinkPlugin } from '@adamdotai/editor/ClickableLinkPlugin'
import { EditableObserverPlugin } from '@adamdotai/editor/EditableObserverPlugin'
import { EditorPlaceholderPlugin } from '@adamdotai/editor/EditorPlaceholderPlugin'
import {
    nodes as HashtagMentionNodes,
    HashtagMentionsPlugin,
} from '@adamdotai/editor/HashtagMentionsPlugin'
import { nodes as LinkNodes, LinkPlugin } from '@adamdotai/editor/LinkPlugin'
import {
    $createAdamParagraphNode,
    AdamParagraphNode,
} from '@adamdotai/editor/NotesPlugin'
import { EditorMetaDataProvider } from '@adamdotai/editor/providers'
import type { EditorCurrentUser, EditorUser } from '@adamdotai/editor/types'
import type { InitialConfigType } from '@lexical/react/LexicalComposer'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import type { EditorState, LexicalEditor } from 'lexical'
import { $getRoot, ParagraphNode } from 'lexical'
import { useTranslations } from 'next-intl'
import React, { useEffect, useMemo, useState } from 'react'
import { useUser } from '@/services/utils/users'
import { EditorTheme } from '../../EditorTheme'
import { CustomFocusPlugin } from './CustomFocusPlugin'
import { EditorInputPlugin } from './EditorInputPlugin'
import { PlainTextPastePlugin } from './PlainTextPastePlugin'
import { RichTextInputActionsArea } from './RichTextInputActionsArea'
import { SendMessagePlugin } from './SendMessagePlugin'

enum EditorDirection {
    RTL = 'rtl',
    LTR = 'ltr',
}

const Placeholder = ({
    isEditable = true,
    value = 'Type your message ...',
}: {
    isEditable: boolean
    value: string
}) => {
    if (isEditable) {
        return (
            <Box
                pointerEvents="none"
                position="absolute"
                top="8px"
                gap="4"
                width="full"
                paddingInline="10px"
            >
                <Text color="neutral">{value}</Text>
            </Box>
        )
    }

    return null
}
const richTextInputNodes = [
    AdamParagraphNode,
    ...AtMentionNodes,
    ...HashtagMentionNodes,
    ...LinkNodes,
    {
        replace: ParagraphNode,
        with() {
            return $createAdamParagraphNode()
        },
    },
]

const generateInitialConfig: (editable: boolean) => InitialConfigType = (
    editable = false,
) => ({
    namespace: 'RichTextInputEditor',
    onError: () => {},
    nodes: richTextInputNodes,
    theme: EditorTheme,
    editable,
})

const CustomAutoFocusPlugin = () => {
    const [editor] = useLexicalComposerContext()

    useEffect(() => {
        if (editor) {
            editor.update(
                () => {
                    const root = $getRoot()
                    const firstChild = root.getFirstChild()
                    firstChild.selectEnd()
                    setTimeout(() => {
                        const rootElement =
                            editor.getRootElement() as HTMLDivElement
                        rootElement.focus({
                            preventScroll: true,
                        })
                    })
                },
                { discrete: true },
            )
        }
    }, [editor])

    return null
}

interface RichTextInputProps {
    meetingId: string
    users?: EditorUser[]
    state?: string
    readOnly?: boolean
    isEditingMode?: boolean
    clearOnSubmit?: boolean
    shouldHidePersonPlugin?: boolean
    shouldHideHashtagPlugin?: boolean
    isDisabled?: boolean
    editorStylesProps?: any
    actionAreaProps?: any
    sendMentionsProps?: {
        contentType: string
        contentItemId?: string
        sidePanelTab?: string
        contentTitle?: string
    }
    direction?: EditorDirection
    onSubmit?: (editor: LexicalEditor, textValue: string) => void
    onCancel?: (editor: LexicalEditor, intialState: EditorState) => void
    submitButtonTestId?: string
    openContentItemsInNewTab?: boolean
    disableMentioningAllMeetingAttendees?: boolean
}

const RichTextInput: React.FC<RichTextInputProps> = ({
    users,
    meetingId,
    state = '',
    isEditingMode = false,
    readOnly = true,
    clearOnSubmit = false,
    shouldHidePersonPlugin = false,
    shouldHideHashtagPlugin = false,
    isDisabled = false,
    actionAreaProps,
    editorStylesProps,
    sendMentionsProps,
    direction: inputDirection,
    onSubmit,
    submitButtonTestId,
    onCancel,
    openContentItemsInNewTab = false,
    disableMentioningAllMeetingAttendees,
}) => {
    const initialConfig = useMemo(
        () => generateInitialConfig(isEditingMode),
        [],
    )

    const { direction } = useTheme()
    const editorDirection = inputDirection ?? direction

    const [initialState, setInitialState] = useState(null)
    const t = useTranslations()
    const { user, userRoleKey } = useUser()

    const currentEditorUser = React.useMemo<EditorCurrentUser>(() => {
        return {
            id: user.id,
            name: user.displayName,
            email: user.email,
            imageUrl: user.imageUrl,
            meetplaceId: user.meetplace.id,
            roleKey: userRoleKey,
            language: user.language,
            timezone: user.ianaTimezoneId,
            source: user.source,
            userStatus: user.userStatus,
        }
    }, [
        user.displayName,
        user.email,
        user.ianaTimezoneId,
        user.id,
        user.imageUrl,
        user.language,
        user.meetplace.id,
        user.source,
        user.userStatus,
        userRoleKey,
    ])

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <EditorMetaDataProvider
                users={users}
                meetingId={meetingId}
                openContentItemsInNewTab={openContentItemsInNewTab}
                timezone={user?.ianaTimezoneId}
                currentUser={currentEditorUser}
            >
                <Stack
                    spacing="0"
                    width="full"
                    position="relative"
                    minH={readOnly ? 'fit-content' : '18'}
                    bg={(!readOnly || isEditingMode) && 'neutral.100'}
                    borderRadius="4px"
                    border={
                        readOnly && isEditingMode
                            ? '1px solid #005CF7'
                            : '1px solid transparent'
                    }
                    _focusWithin={{
                        border: '1px solid #005CF7',
                    }}
                >
                    <EditableObserverPlugin isEditable={isEditingMode} />
                    <RichTextPlugin
                        contentEditable={
                            <Box
                                id="editor-contentEditable-container"
                                dir={editorDirection}
                                sx={{
                                    '& .chat-editor-space': {
                                        width: 'full',
                                        h:
                                            isEditingMode && !readOnly
                                                ? '8'
                                                : 'full',
                                        boxShadow: 'none',
                                        padding: `${isEditingMode ? '2' : '0'}`,
                                        paddingBottom: '0',
                                        overflowY: 'auto',
                                        textAlign: `${
                                            readOnly && !isEditingMode
                                                ? editorDirection === 'rtl'
                                                    ? 'right'
                                                    : 'left'
                                                : 'unset'
                                        }`,
                                    },
                                    ...editorStylesProps,
                                }}
                            >
                                <ContentEditable className="chat-editor-space" />
                            </Box>
                        }
                        placeholder={null}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <PlainTextPastePlugin />

                    <EditorInputPlugin
                        state={state}
                        isEditingMode={isEditingMode}
                        setInitialState={setInitialState}
                    />

                    {(isEditingMode || !readOnly) && (
                        <>
                            {!shouldHidePersonPlugin && (
                                <AtMentionsPlugin
                                    disableEmailsFlag
                                    disableMentioningAllMeetingAttendees={
                                        disableMentioningAllMeetingAttendees
                                    }
                                />
                            )}
                            {!shouldHideHashtagPlugin && (
                                <HashtagMentionsPlugin />
                            )}
                        </>
                    )}

                    {isEditingMode && (
                        <RichTextInputActionsArea
                            readOnly={readOnly}
                            onCancel={onCancel}
                            shouldHidePersonPlugin={shouldHidePersonPlugin}
                            shouldHideHashtagPlugin={shouldHideHashtagPlugin}
                            submitButtonTestId={submitButtonTestId}
                            actionAreaProps={actionAreaProps}
                        />
                    )}
                    <SendMessagePlugin
                        onSubmit={onSubmit}
                        clearOnSubmit={clearOnSubmit}
                        sendMentionsProps={sendMentionsProps}
                    />
                    {(readOnly || isDisabled) && (
                        <CustomFocusPlugin
                            initialState={initialState}
                            isEditingMode={isEditingMode}
                            onCancel={onCancel}
                            isDisabled={isDisabled}
                        />
                    )}
                    <EditorPlaceholderPlugin
                        placeholder={Placeholder({
                            isEditable: true,
                            value: t('meetingroom.type-your-message'),
                        })}
                    />
                    <AutoLinkPlugin />
                    <LinkPlugin />
                    <ClickableLinkPlugin />
                    {isEditingMode ? <CustomAutoFocusPlugin /> : null}
                </Stack>
            </EditorMetaDataProvider>
        </LexicalComposer>
    )
}

export { RichTextInput, EditorDirection }
